import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Package, ExternalLink } from 'lucide-react';
import Container from '../../components/layout/Container';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import Skeleton from '../../components/common/Skeleton';
import { orderService } from '../../services/orderService';
import { paymentService } from '../../services/paymentService';
import { useStoreContext } from '../../contexts/StoreContext';
import { formatPrice } from '../../utils/priceCalculator';
import { formatOrderNumber } from '../../utils/statusBucket';

const OrderConfirmation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { store } = useStoreContext();
  const [order, setOrder] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const o = await orderService.getById(id);
        if (cancelled) return;
        setOrder(o);
        // O backend cria payment automaticamente após order quando o gateway é
        // configurado. Tentamos buscar; se ainda não existir, deixamos null.
        try {
          const list = await paymentService.getAll({ orderId: id });
          const first = list?.rows?.[0] || null;
          setPayment(first);
        } catch { /* ignora */ }
      } catch {
        navigate('/minha-conta/pedidos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const storeBase = store?.slug ? `/fornecedor/${store.slug}` : '';

  if (loading) {
    return (
      <Container>
        <div className="max-w-2xl mx-auto py-12">
          <Skeleton variant="block" height={120} />
          <div className="mt-4">
            <Skeleton variant="block" height={200} />
          </div>
        </div>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <div className="text-center py-12">
          <p className="text-app-text-secondary">Pedido não encontrado.</p>
        </div>
      </Container>
    );
  }

  const orderNumber = formatOrderNumber(order);
  const isPending = (order.statusBucket || 'PENDING') === 'PENDING';

  return (
    <Container>
      <div className="max-w-2xl mx-auto py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-success/15 flex items-center justify-center mb-4">
            <CheckCircle2 size={32} className="text-success" />
          </div>
          <h1 className="text-3xl font-extrabold text-app-text">Pedido recebido!</h1>
          <p className="text-app-text-secondary mt-2">
            Pedido <span className="font-semibold">{orderNumber}</span> registrado com sucesso.
          </p>
        </div>

        <Card className="p-6 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <p className="text-xs text-app-text-secondary uppercase tracking-wide">Status</p>
              <StatusBadge domain="order" status={order.statusBucket || order.status} />
            </div>
            <div className="text-right">
              <p className="text-xs text-app-text-secondary uppercase tracking-wide">Total</p>
              <p className="text-2xl font-extrabold text-primary">
                {formatPrice(parseFloat(order.totalToPay || 0))}
              </p>
            </div>
          </div>

          {payment?.invoiceUrl && isPending && (
            <a
              href={payment.invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button fullWidth size="lg" rightIcon={<ExternalLink size={18} />}>
                Pagar agora
              </Button>
            </a>
          )}

          {payment && !payment.invoiceUrl && isPending && (
            <p className="text-sm text-app-text-secondary text-center">
              Aguardando o link de pagamento. Acesse "Meus pedidos" em alguns instantes.
            </p>
          )}

          {!payment && isPending && (
            <p className="text-sm text-app-text-secondary text-center">
              Seu pedido está aguardando aprovação do fornecedor. Você receberá um email com
              o link de pagamento assim que aprovado.
            </p>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-bold text-app-text mb-4 flex items-center gap-2">
            <Package size={20} /> Itens do pedido
          </h2>
          <ul className="space-y-2">
            {(order.items || order.OrderItems || []).map((it) => (
              <li key={it.id} className="flex justify-between text-sm">
                <span>
                  {it.quantity}× {it.productName || it.Product?.name}
                  {it.variationName && (
                    <span className="text-app-text-secondary"> — {it.variationName}</span>
                  )}
                </span>
                <span className="font-medium">
                  {formatPrice(parseFloat(it.totalPrice || it.unitPrice * it.quantity || 0))}
                </span>
              </li>
            ))}
          </ul>
        </Card>

        <div className="flex gap-3 mt-6">
          <Link to="/minha-conta/pedidos" className="flex-1">
            <Button fullWidth variant="subtle">Meus pedidos</Button>
          </Link>
          <Link to={storeBase ? `${storeBase}/catalogo` : '/catalogo'} className="flex-1">
            <Button fullWidth variant="subtle">Continuar comprando</Button>
          </Link>
        </div>
      </div>
    </Container>
  );
};

export default OrderConfirmation;
